import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquarePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/AppShell";
import { FeatureGate } from "@/components/UpgradeGate";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/assistant/")({
  head: () => ({
    meta: [
      { title: "MyPriorities Assistant — AI partner for your hub" },
      {
        name: "description",
        content:
          "Chat with an AI assistant that knows your pillars, goals, matrix, commitments and habits, and drafts changes for approval.",
      },
      { property: "og:title", content: "MyPriorities Assistant — AI partner for your hub" },
      {
        property: "og:description",
        content: "An AI partner grounded in your own goals, tasks, capacity and habits.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: AssistantIndexRoute,
});

function AssistantIndexRoute() {
  return (
    <FeatureGate
      requires="assistant"
      title="Assistant"
      subtitle="Your AI partner — grounded in your pillars, goals, tasks and habits."
    >
      <AssistantIndex />
    </FeatureGate>
  );
}

function AssistantIndex() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const threads = useQuery({
    queryKey: ["chat_threads"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("chat_threads")
        .select("id, title, updated_at")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const createThread = useMutation({
    mutationFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("Not signed in");
      const { data, error } = await supabase
        .from("chat_threads")
        .insert({ user_id: userData.user.id, title: "New conversation" })
        .select("id")
        .single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({ queryKey: ["chat_threads"] });
      void navigate({ to: "/assistant/$threadId", params: { threadId: id } });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteThread = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("chat_threads").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["chat_threads"] }),
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <AppShell
      title="Assistant"
      subtitle="Your AI partner — grounded in your pillars, goals, tasks and habits."
      action={
        <Button onClick={() => createThread.mutate()} disabled={createThread.isPending}>
          <MessageSquarePlus className="size-4" /> New conversation
        </Button>
      }
    >
      <div className="space-y-2">
        {threads.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading conversations…</p>
        ) : (threads.data?.length ?? 0) === 0 ? (
          <div className="rounded-xl border border-border bg-card p-8 text-center">
            <p className="font-display text-lg font-semibold">No conversations yet</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              Start a conversation to review your week, spot capacity risks, or draft new tasks and
              habits — every change waits for your approval.
            </p>
            <Button className="mt-5" onClick={() => createThread.mutate()}>
              <MessageSquarePlus className="size-4" /> Start chatting
            </Button>
          </div>
        ) : (
          threads.data?.map((thread) => (
            <div
              key={thread.id}
              className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3"
            >
              <button
                type="button"
                className="flex-1 text-left"
                onClick={() =>
                  void navigate({ to: "/assistant/$threadId", params: { threadId: thread.id } })
                }
              >
                <span className="text-sm font-medium">{thread.title}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {new Date(thread.updated_at).toLocaleString()}
                </span>
              </button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Delete conversation"
                onClick={() => deleteThread.mutate(thread.id)}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))
        )}
      </div>
    </AppShell>
  );
}
